import React, { FC } from 'react';
import { NotReadyToBuyOnlineProps } from '.';
import { Link } from '@components/v2/link';
import Icon, { Icons } from '@components/Icon';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { isBrandDS, isBrandAP, ModalVersion, LEAD_FORM_ID } from 'src/constants/main';
import { useDispatch } from 'react-redux';
import useTranslations from '@hooks/useTranslations';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from 'src/context/featureSwitchApp';
import { CONTACT_OPTIONS } from 'src/constants/enums';
import { Pages } from 'src/types/pages';
import { useRouter } from 'next/router';

const NotReadyToBuyOnlineTemplate: FC<NotReadyToBuyOnlineProps> = ({ className }) => {
    const isTestDriveContactEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_CONTACT_OPTIONS,
        CONTACT_OPTIONS.TEST_DRIVE
    );

    const isPhoneContactEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_CONTACT_OPTIONS,
        CONTACT_OPTIONS.PHONE_NUMBER
    );

    const isLeadFormContactEnabled = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_CONTACT_OPTIONS,
        CONTACT_OPTIONS.LEAD_FORM
    );

    const router = useRouter();

    const makeARequestBlackList: string[] = ['/configurable', '/stock', '/trim/stock/[...model]'];

    const loadLeadForm = () => {
        dispatch(
            UIDuck.openModal({
                modalType: UIDuck.MODAL_TYPES.LEAD_FORM,
                data: {
                    page: Pages.basketV2,
                    leadFormId: LEAD_FORM_ID,
                    type: 'LEAD_FORM',
                },
                modalVersion: ModalVersion.v2,
            })
        );
    };

    const requestIcon = () => {
        if (isBrandDS) {
            return Icons.HeadsetV2;
        }

        return !isBrandAP ? Icons.Headset : Icons.Request;
    };

    const handleCall = () => {
        window.location.href = `tel:${t('support.telephone')}`;
    };

    const bookATestDrive = () => {
        window.location.href = t('modal.needAHand.testDrive.url');
    };

    const dispatch = useDispatch();
    const { t } = useTranslations();

    const handleFindDealerModal = () =>
        dispatch(
            UIDuck.openModal({
                data: { data: {} },
                callbacks: { onClick: () => console.log('') },
                modalType: UIDuck.MODAL_TYPES.FIND_DEALER,
                modalVersion: ModalVersion.v2,
            })
        );

    return (
        <div className={className}>
            <span className="title">{t('notReadyToBuyOnlineHeading')}</span>
            <div className="container">
                <div className="wrapper">
                    <div className="innerTitle">
                        <div className="icon">
                            <Icon name={requestIcon()} />
                        </div>
                        <span className="titleContent">{t('notReadyToBuyOnlineTitle')}</span>
                    </div>
                    <div className="innerContent">
                        <div className="innerContentTxt">
                            <span className="contentItem">{t('notReadyToBuyOnlineContentItemAssistanceYouNeed')}</span>
                            <span className="contentItem">{t('notReadyToBuyOnlineContentItemOpenningHours')}</span>
                        </div>
                        {isPhoneContactEnabled && (
                            <Link
                                iconName={Icons.ChevronRight}
                                data-testid="notReadyToBuyOnlineCallUsAt"
                                className="linkButton"
                                onClick={handleCall}
                                txt={`${t('needAHand.phoneLabel')} ${t('support.telephone')}`}
                            >
                                <button onClick={handleCall} className="linkButton">
                                    <span className="txt">
                                        {t('needAHand.phoneLabel')} {t('support.telephone')}
                                    </span>
                                    <Icon
                                        name={Icons.ChevronRight}
                                        className="chevron-right-thin"
                                        width={12}
                                        height={10}
                                    />
                                </button>
                            </Link>
                        )}
                    </div>
                </div>
                <div className="wrapper">
                    <div className="innerTitle">
                        <div className="icon">
                            <Icon name={Icons.PoiCommercialBuilding} />
                        </div>
                        <span className="titleContent">{t('notReadyToBuyOnlineContactYourDealer')}</span>
                    </div>
                    <div className="innerContent">
                        <div className="innerContentTxt">
                            <span className="contentItem">{t('notReadyToBuyOnlineContactYourDealerDescription')}</span>
                        </div>
                        <Link
                            iconName={Icons.ChevronRight}
                            data-testid="notReadyToBuyOnlineCallUsAt"
                            className="linkButton"
                            txt={t('notReadyToBuyOnlineFindYourDealer')}
                            onClick={handleFindDealerModal}
                        >
                            <button className="linkButton" onClick={handleFindDealerModal}>
                                <span className="txt">{t('notReadyToBuyOnlineFindYourDealer')}</span>
                                <Icon name={Icons.ChevronRight} className="chevron-right-thin" width={12} height={10} />
                            </button>
                        </Link>
                        {isTestDriveContactEnabled && (
                            <Link
                                iconName={Icons.ChevronRight}
                                data-testid="notReadyToBuyOnlineCallUsAt"
                                className="linkButton"
                                txt={t('notReadyToBuyOnlineBookATestDrive')}
                                onClick={bookATestDrive}
                            >
                                <button onClick={bookATestDrive} className="linkButton">
                                    <span className="txt">{t('notReadyToBuyOnlineBookATestDrive')}</span>
                                    <Icon
                                        name={Icons.ChevronRight}
                                        className="chevron-right-thin"
                                        width={12}
                                        height={10}
                                    />
                                </button>
                            </Link>
                        )}
                        {!makeARequestBlackList.includes(router.pathname) && isLeadFormContactEnabled && (
                            <Link
                                iconName={Icons.ChevronRight}
                                data-testid="notReadyToBuyOnlineCallUsAt"
                                className="linkButton"
                                txt={t('notReadyToBuyOnlineWriteUs')}
                                onClick={loadLeadForm}
                            >
                                <button onClick={loadLeadForm} className="linkButton">
                                    <span className="txt">{t('notReadyToBuyOnlineWriteUs')}</span>
                                    <Icon
                                        name={Icons.ChevronRight}
                                        className="chevron-right-thin"
                                        width={12}
                                        height={10}
                                    />
                                </button>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default NotReadyToBuyOnlineTemplate;
